import React, { DispatchWithoutAction, FC, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm, UseFormOptions } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { fc } from 'i18n';
import { TrackerField } from './tracker-field';
import { RemarksField } from './remarks-field';
import { RippleIdField } from './ripple-id-field';
import Button from 'core/react/general/form/Button';
import { ModalWrapper } from 'components/molecules/modal-wrapper';
import { actions as vehiclesActions } from 'core/redux/api/veta/airsideVehicles';
import { monitoredVehiclesSelector } from 'core/redux/selectors/monitoredVehicles';
import { IMonitoredVehicle, IMonitoringAssetForm } from 'types/vehicles-monitoring';

import styles from './styles.module.css';

interface IAssetFormProps {
  asset?: IMonitoredVehicle | null;
  page: { no: number; size: number; sort: string | undefined; search: string | undefined };
  close: DispatchWithoutAction;
}

const DEFAULT_FORM_VALUES = { id: '', tracker: null, remarks: '' };

const FORM_OPTIONS: UseFormOptions<IMonitoringAssetForm> = {
  mode: 'onChange',
  defaultValues: DEFAULT_FORM_VALUES,
};

export const AssetForm: FC<IAssetFormProps> = (props) => {
  const { asset, page, close } = props;

  const dispatch = useDispatch();
  const { pending, error } = useSelector(monitoredVehiclesSelector);

  const loadingRef = useRef(false);

  const formMethods = useForm<IMonitoringAssetForm>(FORM_OPTIONS);

  const [formError, setFormError] = useState<string | null>(null);

  const { reset, handleSubmit } = formMethods;

  const setInitialValues = () => {
    if (asset) {
      const { rippleId, deviceInfo, remarks } = asset;
      const { name, uri } = deviceInfo;

      reset({ id: rippleId, tracker: { value: uri, label: name || uri }, remarks });
    }
  };

  const onRequestFulfilled = (error: string | null) => {
    if (error) {
      return setFormError(error);
    }

    setFormError(null);
    close();
  };

  const onSubmit = handleSubmit(({ id, tracker, remarks }: IMonitoringAssetForm) => {
    const payload = { rippleId: id, deviceURI: tracker?.value || '', remarks };

    asset?.id
      ? dispatch(vehiclesActions.update.request({ id: asset.id, vehicle: payload, page }))
      : dispatch(vehiclesActions.add.request({ vehicle: payload, page }));

    loadingRef.current = true;
  });

  useEffect(() => {
    setInitialValues();
  }, []);

  useEffect(() => {
    if (!pending && loadingRef.current) {
      loadingRef.current = false;

      onRequestFulfilled(error);
    }
  }, [pending, error]);

  return (
    <ModalWrapper>
      <FormProvider {...formMethods}>
        <form>
          <div className={styles.wrapper}>
            <div className={styles.title}>{fc('create new asset')}</div>
            <div className={styles.fields}>
              <RippleIdField isLoading={pending} />
              <TrackerField isLoading={pending} />
              <RemarksField isLoading={pending} />
              {!!formError && <div className={styles.error}>{formError}</div>}
            </div>
            <div className={styles.buttons}>
              <Button type="button" onClick={close}>
                {fc('cancel')}
              </Button>
              <Button type="button" loading={pending} onClick={onSubmit}>
                {fc('save')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </ModalWrapper>
  );
};
